import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@shared/ui/molecules/Dialog';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import './delete-user-dialog.scss';
import { UserAvatarAndName } from "@shared/ui/molecules/UserAvatarAndName";
import { closeDialog } from '@shared/store/dialog.duck';
import { BasicUserInfo } from '@shared/models/user.models';
import { deleteUserRequest, permanentlyDeleteUserRequest } from '@features/settings/services/ManagementService';
import { failureNotification } from '@shared/store/notification.duck';
import { NotificationMessages, redirectWithNotification } from '@shared/models/notification.models';
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { useTranslation } from "react-i18next";
import { Center } from "@shared/ui/atoms/StructuralLayout";
import { HeadlineLarge } from "@shared/ui/atoms/fonts/Headline";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import Colours from '@shared/ui/atoms/Colours';

export default function DeleteUserDialog(props: { payload?: { userInfo?: BasicUserInfo } }) {
  const userInfo = props.payload?.userInfo;
  const [isLoading, setIsLoading] = useState(false);
  const [confirmingPermanent, setConfirmingPermanent] = useState(false);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const close = () => {
    dispatch(closeDialog());
  }

  const deleteUser = async () => {
    if (!userInfo) return;
    try {
      setIsLoading(true);
      await deleteUserRequest(userInfo.userId);
      redirectWithNotification(NotificationMessages.userDeleted, "/manage/users?view=user", true)
    } catch (e: any) {
      console.error(e)
      dispatch(failureNotification(e?.message || "User failed to be deleted"))
    } finally {
      setIsLoading(false)
    }
  }

  const permanentlyDeleteUser = async () => {
    if (!userInfo) return;
    try {
      setIsLoading(true);
      await permanentlyDeleteUserRequest(userInfo.userId);
      redirectWithNotification(NotificationMessages.userDeleted, "/manage/users?view=user", true)
    } catch (e: any) {
      console.error(e)
      dispatch(failureNotification(t('delete-user-dialog.permanent-delete-failed')))
    } finally {
      setIsLoading(false)
    }
  }

  if (!userInfo) {
    return null
  }

  return (
    <Dialog isOpen={true} onClose={() => close()} className="deleteUserDialog">
      {isLoading ?
        <LoadingSpinner hideBorder={true}/> :
        confirmingPermanent ?
        <>
          <HeadlineLarge style={{marginBottom: 12}}>{t('delete-user-dialog.permanent-title')}</HeadlineLarge>
          <BodyRegular>{t('delete-user-dialog.permanent-description')}</BodyRegular>
          <Center style={{margin: '16px 0px 40px 0px'}}>
            <UserAvatarAndName className="deleteUserDialog__user" firstName={userInfo.firstName}
                               lastName={userInfo.lastName}/>
          </Center>
          <PrimaryButton className="deleteUserDialog__button"
                         text="delete-user-dialog.confirm-permanent"
                         fullWidth={true}
                         size={ButtonSize.Large}
                         click={permanentlyDeleteUser}/>
          <OutlineButton text="button.reject-delete-user" fullWidth={true} size={ButtonSize.Large}
                         click={() => setConfirmingPermanent(false)}/>
        </> :
        <>
          <HeadlineLarge style={{marginBottom: 12}}>{t('delete-user-dialog.title')}</HeadlineLarge>
          <BodyRegular>{t('delete-user-dialog.description')}</BodyRegular>
          <Center style={{margin: '16px 0px 40px 0px'}}>
            <UserAvatarAndName className="deleteUserDialog__user" firstName={userInfo.firstName}
                               lastName={userInfo.lastName}/>
          </Center>
          <PrimaryButton className="deleteUserDialog__button"
                         text="button.confirm-delete-user"
                         fullWidth={true}
                         size={ButtonSize.Large}
                         click={deleteUser}/>
          <OutlineButton className="deleteUserDialog__button"
                         text="delete-user-dialog.delete-permanently"
                         fullWidth={true}
                         size={ButtonSize.Large}
                         borderColour={Colours.red}
                         click={() => setConfirmingPermanent(true)}/>
          <OutlineButton text="button.reject-delete-user" fullWidth={true} size={ButtonSize.Large} click={close}/>
        </>
      }
    </Dialog>
  )
}
